import * as cornerstone from "@cornerstonejs/core";
import { metaData } from "@cornerstonejs/core";
import { vec3 } from "gl-matrix";
import {
  makeBytesAvailableInCache,
} from "./CornerstoneService";
import hardcodedMetaDataProvider from "./MetadataProvider";
import { isMobile } from "react-device-detect";
import { PREFETCH_IMAGE_CONCURRENCY } from "../../../constants/Constants";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import {
  fetchAndRegisterMultiFrameMetadata,
  getImageMetadata,
  prefetchAdditionalFrames,
  resolvePrimaryFrameImageId,
} from "./MultiFrameService";

// Backstop timeout for a single prefetch load. The wadouri loader can HANG on
// the bare, frame-less base URL of an un-split multi-frame instance; without a
// cap the sliding-window worker wedges forever and Promise.all never resolves,
// so the series never reports preload-complete (bar stuck at ~99%). Sized well
// above a normal decode so it only trips on a genuine stall.
const PREFETCH_IMAGE_TIMEOUT_MS = 20000;

// Thumbnails are rendered to a plain <canvas> via CPU (loadImageToCanvas with
// useCPURendering), NOT as Cornerstone WebGL STACK viewports. Each WebGL
// viewport allocates a GPU context, and the browser caps live contexts
// (~8-16). A study with many series/priors used to exhaust the GPU → "WebGL
// context lost" → the patched renderWindow.render() throws → the "Viewer is
// unable to perform the action" alert (seen on macOS/Metal). This registry
// holds each thumbnail's imageIds/stack so the exported viewport-shaped API
// keeps working without a real viewport.
const thumbnailRegistry = new Map();

// Per-thumbnail prefetch abort flags, keyed by thumbnail index.
const prefetchControllers = new Map();

// Registry/controller keys must be type-stable. displayThumbnail stores with a
// NUMBER (React prop index), but drag-and-drop passes a STRING
// (e.dataTransfer.getData("thumbnailIndex")) and some state defaults are strings
// too. Map.get is type-strict, so normalise every key through Number(). The old
// getViewport("thumbnail_"+index) string-concat lookup was type-agnostic, which
// hid this — a strict Map.get would otherwise miss and leave drag-to-viewport
// blank ("1/NaN").
const thumbKey = (index) => Number(index);

export const prefetchData = async (index, onImageLoaded) => {
  const key = thumbKey(index);
  const data = thumbnailRegistry.get(key);
  if (!data || !Array.isArray(data.imageIds) || data.imageIds.length === 0)
    return;
  makeBytesAvailableInCache();

  // Cancel any in-flight prefetch for this index before starting a new one.
  if (prefetchControllers.has(key)) {
    prefetchControllers.get(key).abort = true;
  }
  const controller = { abort: false };
  prefetchControllers.set(key, controller);

  // Sliding-window loadAndCacheImage — no viewport-based stackPrefetch. Each
  // cached image still emits IMAGE_CACHE_IMAGE_ADDED, so the onImageLoaded
  // listener in Thumbnail.jsx keeps firing for progress.
  //
  // We keep PREFETCH_IMAGE_CONCURRENCY loads IN FLIGHT at all times and start
  // the next imageId the instant one finishes — instead of firing a batch of N
  // and awaiting the WHOLE batch before the next. loadAndCacheImage resolves
  // only after the web-worker DECODE completes (not when the XHR returns), so a
  // batch barrier left the network idle for the full decode time of the slowest
  // image in each batch — the multi-second gaps between request bursts seen even
  // on a single-series study. A sliding window keeps the request lanes and the
  // decode workers saturated with no gaps, and respects the abort flag between
  // every start so a viewer switch still stops it promptly.
  const imageIds = data.imageIds;
  let nextIndex = 0;
  const startNext = () => {
    if (controller.abort || nextIndex >= imageIds.length) return null;
    // Load the primary frame URL for multi-frame instances (?frame=1) instead
    // of the bare base — the frame-less base URL can hang the wadouri loader,
    // and ?frame=1 is the same id the viewport caches, so progress counts it.
    const imageId = resolvePrimaryFrameImageId(imageIds[nextIndex++]);
    // Race the load against a timeout so a stalled load can't wedge this
    // worker (and therefore Promise.all / the whole series preload) forever.
    const load = loadAndCacheImage(imageId).catch(() => {});
    return Promise.race([
      load,
      new Promise((resolve) => setTimeout(resolve, PREFETCH_IMAGE_TIMEOUT_MS)),
    ]).then(() => startNext());
  };
  const windowSize = Math.min(PREFETCH_IMAGE_CONCURRENCY, imageIds.length);
  const workers = [];
  for (let i = 0; i < windowSize; i++) {
    const w = startNext();
    if (w) workers.push(w);
  }
  await Promise.all(workers);
};

export const stopPrefetchData = async (thumbnailIndex, onImageLoaded) => {
  const key = thumbKey(thumbnailIndex);
  const controller = prefetchControllers.get(key);
  if (controller) {
    controller.abort = true;
    prefetchControllers.delete(key);
  }
};

// Abort every in-flight prefetch at once. Used when the SharedViewer switches to
// the Advance (MedDream iframe) viewer: the thumbnail panel is hidden, so the
// background preload loop is paused and any batch still downloading is aborted
// mid-flight so the network goes quiet immediately. Aborted images are simply
// re-fetched (and mostly cache-hit) when preload resumes on switch back.
export const stopAllPrefetchData = () => {
  for (const controller of prefetchControllers.values()) {
    controller.abort = true;
  }
  prefetchControllers.clear();
};

// Decoupling backstop. loadActiveViewport reads imageIds via
// getThumbnailViewport(thumbnailIndex) and early-returns if the registry has no
// entry — which happens when a viewport loads a series whose thumbnail hasn't
// lazily rendered yet (priors / multiple studies), leaving the viewport blank
// and unbound from the tool group. Callers seed the registry from the assigned
// series before loadActiveViewport so the lookup never misses.
//
// Idempotent + fill-only-if-missing: never clobbers an entry displayThumbnail
// already created (which holds the same series for that index), so the
// drag-and-drop path — where the dragged thumbnail is already rendered — keeps
// its real entry even if the caller passes a stale closure stack.
export const registerThumbnailStack = (thumbnailIndex, stack) => {
  if (!stack || !Array.isArray(stack.imageIds) || stack.imageIds.length === 0)
    return;
  const key = thumbKey(thumbnailIndex);
  const existing = thumbnailRegistry.get(key);
  if (existing && Array.isArray(existing.imageIds) && existing.imageIds.length)
    return;
  thumbnailRegistry.set(key, {
    imageIds: stack.imageIds,
    stack: stack,
  });
};

// Draw the first frame of a grayscale image straight to `canvas` with a linear
// VOI window, bypassing cornerstone's CPU loadImageToCanvas (whose integer LUT
// renders black for float pixel data — see the call site in displayThumbnail).
//
// Returns true when it rendered, false when it declined (color image, missing
// pixel data, or any error) so the caller can fall back to loadImageToCanvas.
const renderGrayscaleThumbnail = async (canvas, imageId) => {
  try {
    const image = await loadAndCacheImage(imageId);
    if (!image || image.color) return false;

    const width = image.columns || image.width;
    const height = image.rows || image.height;
    if (!width || !height) return false;

    const pixelData =
      (image.voxelManager && image.voxelManager.getScalarData
        ? image.voxelManager.getScalarData()
        : null) ||
      (image.getPixelData ? image.getPixelData() : null);
    if (!pixelData || pixelData.length < width * height) return false;

    // If the loader already applied the modality LUT (preScale), the scalar
    // data is in rescaled space — which is the space WindowCenter/WindowWidth
    // are defined in — so apply rescale only when it didn't.
    const prescaled = !!(image.preScale && image.preScale.scaled);
    const slope = prescaled ? 1 : Number(image.slope) || 1;
    const intercept = prescaled ? 0 : Number(image.intercept) || 0;

    const num = (v) => (Array.isArray(v) ? v[0] : v);
    let ww = Number(num(image.windowWidth));
    let wc = Number(num(image.windowCenter));
    if (!Number.isFinite(ww) || ww <= 0 || !Number.isFinite(wc)) {
      // No usable VOI in metadata — derive one from the rescaled pixel range.
      const min = (Number(image.minPixelValue) || 0) * slope + intercept;
      const max = (Number(image.maxPixelValue) || 0) * slope + intercept;
      ww = max - min || 1;
      wc = (max + min) / 2;
    }

    // DICOM linear VOI LUT (PS3.3 C.11.2.1.2).
    const lo = wc - 0.5 - (ww - 1) / 2;
    const scale = 255 / (ww - 1);

    // MONOCHROME1 images display low pixel values as WHITE — the dicom-image-loader
    // flags this on the image object (createImage.js sets invert = photometricInterpretation
    // === 'MONOCHROME1'), and the main viewport's WebGL shader honors it automatically.
    // This manual CPU renderer bypasses that shader, so without this check MONOCHROME1
    // thumbnails render inverted relative to the correctly-displayed main viewport.
    const invert = !!image.invert;

    const buffer = document.createElement("canvas");
    buffer.width = width;
    buffer.height = height;
    const bctx = buffer.getContext("2d");
    const imageData = bctx.createImageData(width, height);
    const out = imageData.data;
    const count = width * height;
    for (let i = 0; i < count; i++) {
      const value = pixelData[i] * slope + intercept;
      let gray;
      if (value <= lo) gray = invert ? 255 : 0;
      else if (value > lo + ww) gray = invert ? 0 : 255;
      else {
        gray = ((value - lo) * scale) | 0;
        if (invert) gray = 255 - gray;
      }
      const o = i * 4;
      out[o] = gray;
      out[o + 1] = gray;
      out[o + 2] = gray;
      out[o + 3] = 255;
    }
    bctx.putImageData(imageData, 0, 0);

    // Match loadImageToCanvas's drawing-buffer convention so the downstream
    // canvas.style sizing in displayThumbnail behaves identically.
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(buffer, 0, 0);
    return true;
  } catch (err) {
    console.debug("[Thumbnail] manual grayscale render failed", imageId, err);
    return false;
  }
};

export const displayThumbnail = async (
  element,
  index,
  stack,
  stackSize,
  isPriors,
  priorStudyData,
  studyIndex,
  setThumbnailLoaded
) => {
  // Keep imageIds/stack in the registry instead of creating a WebGL viewport.
  thumbnailRegistry.set(thumbKey(index), {
    imageIds: stack.imageIds,
    stack: stack,
  });

  cornerstone.metaData.addProvider(
    (type, imageId) => hardcodedMetaDataProvider(type, imageId, stack.imageIds),
    10000
  );

  // Defensive filter — Cornerstone's wadouri metaData provider crashes on
  // any undefined/non-string entry in the stack (it calls parseImageId
  // before checking the scheme). See CornerstoneViewportService for the
  // same guard.
  const safeImageIds = Array.isArray(stack.imageIds)
    ? stack.imageIds.filter((id) => typeof id === "string" && id.length > 0)
    : [];
  if (Array.isArray(stack.imageIds) && safeImageIds.length !== stack.imageIds.length) {
    console.warn(
      "[MultiFrame] thumbnail dropped",
      stack.imageIds.length - safeImageIds.length,
      "invalid imageId entries (was length",
      stack.imageIds.length,
      ")"
    );
  }
  if (safeImageIds.length === 0) {
    console.warn("[MultiFrame] thumbnail stack empty after filtering — skipping setStack");
    return;
  }

  // For any wadors:.../frames/1 slot in the stack (un-split multi-frame
  // instances), make sure the DICOMweb /metadata fetch has completed and
  // is registered with the wadors loader BEFORE setStack — otherwise the
  // loader has no rows/cols/bits to decode frame 1 with. The helper is
  // idempotent per SOP UID and returns the cached promise on repeat
  // calls; fetches for different instances run in parallel.
  await ensureWadorsMetadataReady(safeImageIds);

  // Render the first frame to a plain <canvas> on the CPU — no WebGL context,
  // no rendering engine. The canvas lives inside the passed #thumbnail div.
  try {
    let canvas = element.querySelector("canvas");
    if (!canvas) {
      canvas = document.createElement("canvas");
      element.appendChild(canvas);
    }

    // Render frame 1 ourselves for grayscale images instead of going through
    // cornerstone's CPU loadImageToCanvas.
    //
    // Why: the main viewport is a WebGL stack viewport (shader-based VOI), so it
    // renders any image. The thumbnail CPU path goes
    // loadImageToCanvas -> renderToCanvasCPU -> renderGrayscaleImage, which
    // builds an INTEGER-indexed LUT and looks up lut[pixelData[i]]. The
    // dicom-image-loader pre-scales pixel data by the modality LUT
    // (preScale.enabled defaults to true); when RescaleSlope is non-integer
    // (e.g. Philips MR with slope 1.4112…) the rescaled values are non-integer,
    // so the loader stores them in a Float32Array. lut[<float>] on a typed array
    // is `undefined` -> written as 0 alpha for every pixel -> the canvas stays
    // the black fill. The GPU viewport is immune because it windows in-shader.
    // Result: studies with a non-integer RescaleSlope showed all-black
    // thumbnails while the same series looked fine in the viewport.
    //
    // Drawing the grayscale frame directly (apply modality rescale if the loader
    // didn't, then a linear VOI window) sidesteps the broken integer LUT and
    // handles float or integer pixel data. Color images have no such LUT issue,
    // so they keep using loadImageToCanvas.
    // Render the primary frame URL for a multi-frame first-instance (?frame=1),
    // not the bare frame-less base: the base URL HANGS the wadouri loader
    // (→ the 15s displayThumbnail watchdog fires: "timed out after 15000ms",
    // seen on US priors) and is a DIFFERENT cache key than the ?frame=1 the
    // viewport/prefetch warmed — so rendering the base both stalls and
    // re-downloads instead of reusing the warmed image. Same id
    // resolvePrimaryFrameImageId feeds the prefetch path; single-frame and
    // wadors stacks are returned unchanged.
    const thumbnailFrameId = resolvePrimaryFrameImageId(safeImageIds[0]);
    const renderedManually = await renderGrayscaleThumbnail(
      canvas,
      thumbnailFrameId
    );
    if (!renderedManually) {
      await cornerstone.utilities.loadImageToCanvas({
        canvas: canvas,
        imageId: thumbnailFrameId,
        useCPURendering: true,
        thumbnail: true,
      });
    }

    // loadImageToCanvas sets canvas.style.width/height to fixed pixel values
    // (256 / devicePixelRatio) which overflow the fixed-height thumbnail box
    // (75px) — on a retina display that's a ~128px square spilling out. Re-apply
    // the fill sizing AFTER it renders so the canvas scales to the #thumbnail
    // box with the image contained (aspect preserved). The drawing buffer
    // (canvas.width/height) is left untouched so the bitmap stays sharp.
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.objectFit = "contain";
    canvas.style.display = "block";
  } catch (error) {
    console.log("Thumbnail render error for index", index, error);
  }

  // Initial multi-frame preload at thumbnail load is intentionally
  // disabled — it triggered up-front prefetches for EVERY multi-frame
  // instance across the study (incl. priors), saturating the browser's
  // HTTP/1.1 socket pool and causing net::ERR_INSUFFICIENT_RESOURCES.
  // The active instance's frames are still warmed on demand by
  // prefetchFrameImageIds (called from CornerstoneViewportService when
  // the series opens in the main viewport), which is enough for smooth
  // cine play. Re-enabling this would need a coordinated rate limiter.
  // prefetchRemainingFramesForStack(safeImageIds);

  if (isMobile && index === 0) {
    setThumbnailLoaded(true);
    return;
  }

  if (!isPriors) {
    if (index === stackSize - 1) {
      setThumbnailLoaded(true);
    }
  } else {
    if (priorStudyData.length - 1 === studyIndex) setThumbnailLoaded(true);
  }
};

// Walks a stack's imageIds and awaits one /metadata fetch per wadors
// instance. Single-frame (wadouri) slots are skipped — they need no
// metadata bridge. Returns a Promise that resolves when every needed
// metadata entry is in dicomImageLoader.wadors.metaDataManager (or has
// failed and been logged).
const ensureWadorsMetadataReady = async (imageIds) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) return;
  const seenSops = new Set();
  const pending = [];
  for (const imageId of imageIds) {
    if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) continue;
    const meta = getImageMetadata(imageId);
    if (!meta || !meta.studyUid || !meta.seriesUid || !meta.sopUid) continue;
    if (seenSops.has(meta.sopUid)) continue;
    seenSops.add(meta.sopUid);
    pending.push(
      fetchAndRegisterMultiFrameMetadata({
        studyId: meta.studyId,
        studyUid: meta.studyUid,
        seriesUid: meta.seriesUid,
        sopUid: meta.sopUid,
        numberOfFrames: meta.numberOfFrames,
      })
    );
  }
  if (pending.length) {
    await Promise.all(pending);
  }
};

// Walks a stack's imageIds and fires background prefetches for frames
// 2..N of each multi-frame instance. Frame 1 has already been fetched by
// setStack; this fills the cache for cine playback. Fire-and-forget.
const prefetchRemainingFramesForStack = (imageIds) => {
  if (!Array.isArray(imageIds) || imageIds.length === 0) return;
  const seenSops = new Set();
  for (const imageId of imageIds) {
    if (typeof imageId !== "string" || !imageId.startsWith("wadors:")) continue;
    const meta = getImageMetadata(imageId);
    if (!meta || !meta.studyUid || !meta.seriesUid || !meta.sopUid) continue;
    if (!Number.isInteger(meta.numberOfFrames) || meta.numberOfFrames < 2) continue;
    if (seenSops.has(meta.sopUid)) continue;
    seenSops.add(meta.sopUid);
    prefetchAdditionalFrames({
      studyId: meta.studyId,
      studyUid: meta.studyUid,
      seriesUid: meta.seriesUid,
      sopUid: meta.sopUid,
      numberOfFrames: meta.numberOfFrames,
    });
  }
};

// Lightweight viewport-shaped shim from the registry. Consumers only read
// `.imageIds` (and the truthy guard), so this keeps the old API working
// without a real Cornerstone viewport.
// `index` is the real registry key. getAllThumbnailViewports returns entries in
// LOAD order, not index order, so callers that need the thumbnail number must
// read it from here rather than infer it from the array position.
const toViewportShim = (index, data) => ({
  imageIds: data.imageIds,
  getImageIds: () => data.imageIds,
  id: "thumbnail_" + index,
  index,
});

export const getThumbnailViewport = async (index) => {
  const key = thumbKey(index);
  const data = thumbnailRegistry.get(key);
  if (!data) return null;
  return toViewportShim(key, data);
};

export const getAllThumbnailViewports = async () => {
  const result = [];
  for (const [index, data] of thumbnailRegistry) {
    result.push(toViewportShim(index, data));
  }
  return result;
};

export const checkIfLateralSeries = async (thumbnailIndex) => {
  const data = thumbnailRegistry.get(thumbKey(thumbnailIndex));
  if (!data || !Array.isArray(data.imageIds) || data.imageIds.length === 0)
    return false;
  const imageId = await loadAndCacheImage(data.imageIds[0]);
  const imagePlane = metaData.get("imagePlaneModule", imageId.imageId);
  if (imagePlane && imagePlane.rowCosines && imagePlane.columnCosines) {
    const row = [
      Math.round(imagePlane.rowCosines[0]),
      Math.round(imagePlane.rowCosines[1]),
      Math.round(imagePlane.rowCosines[2])
    ];
    const column = [
      Math.round(imagePlane.columnCosines[0]),
      Math.round(imagePlane.columnCosines[1]),
      Math.round(imagePlane.columnCosines[2])
    ];
    const normal = [];
    vec3.cross(normal, column, row);

    if (Math.abs(normal.x) === 1)
      return true;
  }
  return false;
};
